import { createVeriffFrame, MESSAGES } from '@veriff/incontext-sdk';
import IdCardIcon from 'assets/icons/id_card.svg?react';
import LockIcon from 'assets/icons/lock.svg?react';
import SmartphoneIcon from 'assets/icons/smartphone.svg?react';
import CompanyLogo from 'components/molecules/CompanyLogo/CompanyLogo';
import Container from 'components/molecules/Container/Container';
import { appSwitchRoutes, getRoleHomeUrl } from 'constants/routes';
import { identityVerificationDataUsageLink } from 'constants/identityVerificationLink';
import { supportEmail } from 'constants/supportEmail';
import {
  useInitiateVerificationMutation,
  useMyRoleQuery,
  useUserQuery,
  VerificationStatus,
} from 'generated/graphql';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Link,
  Loader,
  NavLink,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import chromeStyles from '../AccessibilityAdjustments/AccessibilityAdjustments.module.scss';
import styles from './RoleStageIdentityVerification.module.scss';

/**
 * Identity-verification step (Veriff) for the roles flow.
 *
 * Reached when the stage-actions query is rejected with
 * IDENTITY_VERIFICATION_REQUIRED — a project with `idVerificationEnabled`
 * whose candidate is not yet globally approved. Launches the Veriff
 * in-context SDK, then polls our backend (`me.identityVerificationStatus`)
 * for the asynchronous decision — the SDK only reports flow completion,
 * never the verdict.
 *
 * The screen renders four candidate-facing states beneath the shared roles
 * chrome (stage-list nav + company logo + role name):
 *   intro     → "Verify your identity to continue" + requirements
 *   verifying → "Verifying your identity" (spinner, while polling)
 *   retry     → "We couldn't verify your identity" (resubmission requested)
 *               with a manual restart that reuses the same Veriff session
 *   terminal  → "We still couldn't verify your identity" (declined — Veriff's
 *               final verdict, no retry)
 */

type Phase = 'intro' | 'verifying' | 'retry' | 'terminal' | 'error';

const POLL_INTERVAL_MS = 2500;

const RoleStageIdentityVerification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { roleId, stageId } = useParams();

  const destination =
    (location.state as { from?: string } | null)?.from ??
    appSwitchRoutes.roleStageActions.getUrl(
      roleId as string,
      stageId as string
    );

  const [phase, setPhase] = useState<Phase>('intro');
  const [isPolling, setIsPolling] = useState(false);

  const frameRef = useRef<{ close: () => void } | null>(null);
  // Derives the initial state from the server status exactly once, so a returning
  // candidate lands on the right screen (e.g. a capped user sees the terminal
  // state, not the intro) without clobbering later user-driven transitions.
  const initializedRef = useRef(false);

  const { handleMsgType } = useNotification();

  const [initiateVerification] = useInitiateVerificationMutation();

  const { data: userData } = useUserQuery({
    fetchPolicy: 'network-only',
    pollInterval: isPolling ? POLL_INTERVAL_MS : 0,
  });
  const status = userData?.User.identityVerificationStatus;

  // Company logo + role name for the chrome. MyRole is readable without
  // identity approval — only the stage `actions` field is gated.
  const { data: myRoleData } = useMyRoleQuery({
    variables: { id: roleId as string },
    skip: !roleId,
  });
  const role = myRoleData?.myRole;

  const backUrl = getRoleHomeUrl(
    roleId as string,
    role?.candidatePortalEnabled
  );

  // Always tear the Veriff frame down when leaving the view.
  useEffect(() => {
    return () => frameRef.current?.close();
  }, []);

  const handleFrameEvent = useCallback((msg: MESSAGES) => {
    switch (msg) {
      case MESSAGES.STARTED:
        setPhase('verifying');
        break;
      case MESSAGES.SUBMITTED:
      case MESSAGES.FINISHED:
        // Flow finished on the client — the decision still arrives via webhook.
        frameRef.current?.close();
        setPhase('verifying');
        setIsPolling(true);
        break;
      case MESSAGES.CANCELED:
        // Closing the frame isn't a failed attempt; let them start over.
        frameRef.current?.close();
        setPhase('intro');
        break;
      default:
        break;
    }
  }, []);

  const launchFrame = useCallback(
    (url: string) => {
      frameRef.current?.close();
      frameRef.current = createVeriffFrame({
        url,
        onEvent: handleFrameEvent,
        // Older iOS Safari / in-app webviews can blank the frame and ask for a
        // reload — recreate it on the same session URL.
        onReload: () => {
          frameRef.current?.close();
          frameRef.current = createVeriffFrame({
            url,
            onEvent: handleFrameEvent,
          });
        },
      });
    },
    [handleFrameEvent]
  );

  const handleStart = useCallback(async () => {
    setPhase('verifying');
    try {
      const res = await initiateVerification();
      const session = res.data?.initiateVerification;
      if (!session) {
        setPhase('error');
        return;
      }
      if (session.status === VerificationStatus.Approved) {
        navigate(destination, { replace: true });
        return;
      }
      // The backend returns no session URL for a terminal (declined) user.
      if (!session.sessionUrl) {
        setPhase('terminal');
        return;
      }
      launchFrame(session.sessionUrl);
    } catch {
      setPhase('error');
    }
  }, [initiateVerification, launchFrame, navigate, destination]);

  // One-shot initial state derivation from the server status.
  useEffect(() => {
    if (initializedRef.current || !userData) return;
    initializedRef.current = true;

    if (status === VerificationStatus.Approved) {
      navigate(destination, { replace: true });
      return;
    }
    // A decline is Veriff's final verdict — terminal, no retry.
    if (status === VerificationStatus.Declined) {
      setPhase('terminal');
      return;
    }
    if (status === VerificationStatus.ResubmissionRequested) {
      setPhase('retry');
      return;
    }
    if (
      status === VerificationStatus.Pending ||
      status === VerificationStatus.Review
    ) {
      // A session is already in flight — resume polling for its verdict.
      setPhase('verifying');
      setIsPolling(true);
    }
  }, [userData, status, destination, navigate]);

  // React to the asynchronous decision while polling.
  useEffect(() => {
    if (!isPolling || !status) return;
    switch (status) {
      case VerificationStatus.Approved:
        setIsPolling(false);
        handleMsgType({
          type: TNotification.success,
          message: t(
            'identityVerification.successNotification',
            'Your ID document has been successfully verified.'
          ),
        });
        navigate(destination, { replace: true });
        break;
      case VerificationStatus.Declined:
        // Final verdict from Veriff — terminal, no further attempts.
        setIsPolling(false);
        setPhase('terminal');
        break;
      case VerificationStatus.ResubmissionRequested:
        setIsPolling(false);
        setPhase('retry');
        break;
      // Pending / Review / Expired / Abandoned → keep polling for a verdict.
      default:
        break;
    }
  }, [isPolling, status, destination, navigate, handleMsgType, t]);

  return (
    <Container
      className={chromeStyles.container}
      dataTestId="identity-verification"
    >
      <header>
        <nav className="navbar">
          <NavLink
            to={backUrl}
            onClick={e => {
              e.preventDefault();
              navigate(backUrl);
            }}
            aria-label={t('roles.backToStageList')}
            direction="backward"
          >
            {t('roles.stageList')}
          </NavLink>
        </nav>
      </header>
      <main className={chromeStyles.content} aria-live="polite">
        <div className={chromeStyles.details}>
          <CompanyLogo
            companyLogoUrl={role?.company.logoUrl}
            companyName={role?.company.name ?? ''}
          />
          <h1 className={chromeStyles.title} tabIndex={-1}>
            {role?.name}
          </h1>
        </div>

        {phase === 'intro' && (
          <div className={styles.state}>
            <h2 className={styles.title}>
              {t(
                'identityVerification.title',
                'Verify your identity to continue'
              )}
            </h2>
            <p className={styles.body}>
              {t('identityVerification.introBody', {
                defaultValue:
                  '{{company}} asks every candidate for this role to confirm their identity to keep the process fair.',
                company:
                  role?.company.name || t('common.thisCompany', 'This company'),
              })}
            </p>

            <div className={styles.requirements}>
              <div className={styles.requirementsHeader}>
                {t('identityVerification.requirementsHeading', 'Requirements')}
              </div>
              <div className={styles.requirementRow}>
                <span className={styles.requirementIcon}>
                  <IdCardIcon aria-hidden focusable="false" />
                </span>
                <span>
                  {t(
                    'identityVerification.requirementIdDocument',
                    'A valid identity document'
                  )}
                </span>
              </div>
              <div className={styles.requirementRow}>
                <span className={styles.requirementIcon}>
                  <SmartphoneIcon aria-hidden focusable="false" />
                </span>
                <span>
                  {t('identityVerification.requirementSmartphone', 'Smartphone')}
                </span>
              </div>
            </div>

            <div className={styles.secureNote}>
              <p className={styles.secureNoteText}>
                <LockIcon
                  className={styles.secureIcon}
                  aria-hidden
                  focusable="false"
                />
                {t(
                  'identityVerification.secureNote',
                  'Handled by a secure verification provider.'
                )}
              </p>
              <Link to={identityVerificationDataUsageLink} target="_blank">
                {t(
                  'identityVerification.secureNoteLink',
                  'How your data is used.'
                )}
              </Link>
            </div>

            <Button onClick={handleStart}>
              {t('identityVerification.start', 'Start identity verification')}
            </Button>
          </div>
        )}

        {phase === 'verifying' && (
          <div className={styles.state}>
            <h2 className={styles.title}>
              {t('identityVerification.verifyingTitle', 'Verifying your identity')}
            </h2>
            <div className={styles.spinner}>
              <Loader size='large' />
            </div>
            <p className={styles.body}>
              {t(
                'identityVerification.verifyingBody',
                'This can take a moment, please keep this page open. It will be updated automatically once the verification process is complete.'
              )}
            </p>
            <Button loading disabled>
              {t(
                'identityVerification.verifyingButton',
                'Waiting for the verification provider'
              )}
            </Button>
          </div>
        )}

        {phase === 'retry' && (
          <div className={styles.state}>
            <h2 className={styles.title}>
              {t(
                'identityVerification.retryTitle',
                'We couldn’t verify your identity'
              )}
            </h2>
            <p className={styles.body}>
              {t(
                'identityVerification.retryBody',
                'We weren’t able to confirm your identity, so you can’t continue with this stage just yet. You can try again below.'
              )}
            </p>
            <Button onClick={handleStart}>
              {t(
                'identityVerification.restart',
                'Restart identity verification'
              )}
            </Button>
            <p className={styles.contactPrompt}>
              {t('identityVerification.contactPrompt', 'Think this is a mistake?')}{' '}
              <Link to={`mailto:${supportEmail}`} target="_blank">
                {t('identityVerification.contactPromptLink', 'Contact us')}
              </Link>
            </p>
          </div>
        )}

        {phase === 'terminal' && (
          <div className={styles.state}>
            <h2 className={styles.title}>
              {t(
                'identityVerification.terminalTitle',
                'We still couldn’t verify your identity'
              )}
            </h2>
            <p className={styles.contactPrompt}>
              {t('identityVerification.contactPrompt', 'Think this is a mistake?')}{' '}
              <Link to={`mailto:${supportEmail}`} target="_blank">
                {t('identityVerification.contactPromptLink', 'Contact us')}
              </Link>
            </p>
          </div>
        )}

        {phase === 'error' && (
          <div className={styles.state}>
            <h2 className={styles.title}>
              {t('identityVerification.errorTitle', 'Something went wrong')}
            </h2>
            <p className={styles.body}>
              {t(
                'identityVerification.errorBody',
                'We couldn’t start identity verification. Please try again.'
              )}
            </p>
            <Button onClick={handleStart}>
              {t('identityVerification.tryAgain', 'Try again')}
            </Button>
          </div>
        )}
      </main>
    </Container>
  );
};

export default RoleStageIdentityVerification;
